import {FormState, FormStateWithIssues} from '@raison/form-models';
import {useLocationState} from '@raison/navigation';
import {RootLayout, RsnAmount, RsnAmountInput, RsnButton, RsnDialog, RsnPicker} from '@raison/ui-kit';
import * as Schema from 'effect/Schema';
import React, {useReducer} from 'react';
import {NavigateOptions, useNavigate} from 'react-router';
import {createCurrencyFormatter, useCurrencyFormat} from '~/entities/currencies';
import {GenericErrorMsg} from '~/entities/dialogs';
import {OperationDetail, OperationHeader} from '~/entities/operations-fiat';
import {checkNativeStripeAvailability, useAddNewCardIOS} from '~/processes/cards/api/stripeNative';
import {PaymentSystemFeeDialog} from '~/processes/fiat-operations/ui/Shared/PaymentSystemFeeDialog';
import {OperableAccount, OperableTopupMethod} from '~/redux/finances/domain';
import {useAppDispatch} from '~/shared/store';
import {useDebouncedTopupFee} from '../../api/topup/topupFee';
import {useTopupRequisitesPrefetch} from '../../api/topup/topupRequisites';
import {useTopupCard} from '../../api/topup/topupStripe';
import {transformFlagsToHint} from '../../model/transformFlagsToHint';
import {AddressDialog} from '../../ui/AddressDialog';
import {IbanDialog} from '../../ui/IbanDialog';
import {LabelWithInfo} from '../../ui/LabelWithInfo';
import {OperationInfoSkeleton} from '../../ui/OperationInfoSkeleton';
import CardLimitModal from '../../ui/Topup/CardLimitModal/CardLimitModal';
import StripeErrorDialog from '../../ui/Topup/StripeErrorDialog';
import TopupCardFeeDialog from '../../ui/Topup/TopupCardFeeDialog';
import {Params} from './TopupPage';

type OperationIntroProps = {
    providerName: string;
    accounts: OperableAccount[];
    methods: OperableTopupMethod[];
    account: OperableAccount;
    method: OperableTopupMethod | undefined;
    onOperationChange: (account: OperableAccount, method: OperableTopupMethod | null) => void;
    onStepChange: (amount: RsnAmount, params: Params, options?: NavigateOptions) => void;
    initialAmount: RsnAmount;
};

type TopupFormState = ReturnType<typeof TopupFormState>;
const TopupFormState = (initial: {amount: RsnAmount} | null) =>
    FormState.from({
        amount: initial?.amount || RsnAmount.empty(),
    });

type Values = FormState.inferValues<TopupFormState>;

export function TopupIntro({
    providerName,
    accounts,
    methods,
    account,
    method,
    onOperationChange,
    onStepChange,
    initialAmount,
}: OperationIntroProps) {
    const {format: formatAsSubject} = useCurrencyFormat(account);

    const handleAccountChange = (account: OperableAccount) => onOperationChange(account, method || null);
    const handleMethodChange = (method: OperableTopupMethod) => onOperationChange(account, method);

    useTopupRequisitesPrefetch({
        accountId: account.id,
        methodType: method?.type,
        methodId: method?.id,
    });

    const accountsOptions: RsnPicker.OptionObject<OperableAccount>[] = accounts.map(account => {
        const {format: formatAsPickerOptionBalance} = createCurrencyFormatter({
            code: account.currencyCode,
            decimals: account.currencyDecimals,
        });
        return {
            value: account,
            icon: {src: account.icon},
            title: account.currencyName,
            hint: `Balance: ${formatAsPickerOptionBalance(account.balance)} `,
        };
    });

    const methodsOptions: RsnPicker.OptionObject<OperableTopupMethod>[] = methods
        // Card with id 0 is a new card option
        .filter(method => (method.type === 'card' ? method.id !== 0 : true))
        .map(method => {
            return {
                value: method,
                icon: {src: method.icon},
                title: method.name,
                ...transformFlagsToHint(
                    method.flags,
                    method.type === 'card' ? 'May take a few minutes' : method.description
                ),
            };
        });

    // Card with id 0 is a new card option
    const canAddNewCard = methods.some(method => method.type === 'card' && method.id === 0);

    const {
        handleSubmit: submitTopupCard,
        canSubmit: canSubmitWithCard,
        limitsOptions,
        isLimitsModalOpen,
        setIsLimitsModalOpen,
        error,
    } = useTopupCard(account, {
        onSuccess(orderId) {
            onStepChange(fields.amount.value, {step: 'finish', orderId});
        },
    });

    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const {addNewCard, isPending: cardAddingPending} = useAddNewCardIOS({
        onSuccess: () => navigate('/cards'),
        onError: () => dispatch(GenericErrorMsg.open()),
    });

    const [form, dispatchForm] = useReducer(
        FormState.reduce<Values>,
        {
            amount: initialAmount,
        },
        TopupFormState
    );

    const {
        data: {value: fee} = {},
        isLoading: isFeesLoading,
        isDebouncing: isFeeDebouncing,
    } = useDebouncedTopupFee({
        accountId: account.id,
        methodType: method?.type,
        methodId: method?.id,
        amount: RsnAmount.toString(form.fields.amount.value) || '0',
    });

    const t = {
        accountLabel: 'To',
        methodLabel: 'Using',
        headerSubtitle: `${providerName} provider`,
        minHint: isFeesLoading
            ? 'Loading...'
            : `Min: ${fee?.minimalAmountExclusive ? 'more than ' : ''}${formatAsSubject(fee?.minimalAmount ?? 0)}`,
        feeLabel: 'Fee',
        fee: formatAsSubject(fee?.fee ?? 0),
        totalLabel: (() => {
            if (fee == null) {
                return 'Total';
            }
            return fee.commissionAbove ? 'Total spend' : 'Total receive';
        })(),
        total: formatAsSubject(fee?.total ?? 0),
    };

    const formWithIssues = FormStateWithIssues.fromRules(form, {
        amount: [
            fee?.minimalAmountExclusive
                ? RsnAmount.validateMinExclusive(fee.minimalAmount, t.minHint)
                : RsnAmount.validateMin(fee?.minimalAmount || 0, t.minHint),
        ],
    });

    const handleNewCard = () => {
        if (checkNativeStripeAvailability()) {
            addNewCard();
        } else {
            navigate('/cards/add');
        }
    };

    const {fields, isSubmitDisabled, onSubmit} = FormState.selectFormView(formWithIssues, {
        dispatch: dispatchForm,
        async submit() {
            if (method?.flags?.some(f => f.reasonCode === 'ADDRESS_UNVERIFIED')) {
                setDialog('address');
                return;
            }
            if (method?.flags?.some(f => f.reasonCode === 'IBAN_PENDING')) {
                setDialog('iban');
                return;
            }
            if (method?.type === 'payment_network') {
                onStepChange(fields.amount.value, {step: 'requisites'});
                return true;
            }
            if (method?.type === 'card') {
                submitTopupCard(account, method, fields.amount.value);
            }
        },
    });

    const canSubmit =
        !isSubmitDisabled &&
        !!method &&
        !cardAddingPending &&
        canSubmitWithCard &&
        !isFeesLoading &&
        !isFeeDebouncing &&
        !(method?.type === 'card' && method.id === 0);

    const [dialog, setDialog] = useLocationState('topup-intro-dialog', Schema.Literal('fee', 'iban', 'address'));
    const closeDialog = () => navigate(-1);

    const showFeeInfo = method?.type === 'card' || !!fee?.showFeeInfo;

    return (
        <RootLayout title={'Top up'} pageHeader={<OperationHeader subtitle={t.headerSubtitle} />}>
            <form
                className={'flex flex-1 flex-col gap-6 p-4'}
                onSubmit={e => {
                    e.preventDefault();
                    onSubmit(e);
                }}
            >
                <RsnPicker
                    title={'To account'}
                    label={t.accountLabel}
                    onChange={handleAccountChange}
                    options={accountsOptions}
                    value={account}
                    keyedBy={'id'}
                    disabled={accounts.length <= 1}
                />
                <RsnPicker
                    title={'Top up method'}
                    label={t.methodLabel}
                    onChange={handleMethodChange}
                    options={methodsOptions}
                    value={method}
                    keyedBy={'id'}
                    footer={
                        canAddNewCard && (
                            <RsnButton.Secondary onClick={handleNewCard}>+ Add new card</RsnButton.Secondary>
                        )
                    }
                    disabled={canAddNewCard ? !methods.length : methods.length <= 1}
                />
                <div className={'mt-auto'}>
                    <RsnAmountInput
                        {...fields.amount}
                        helperText={t.minHint}
                        maxDecimals={account.currencyDecimals}
                        maxIntegers={10}
                        suffix={account.currencyCode}
                    />
                </div>
                <div className={'flex flex-col gap-4'}>
                    <OperationDetail.Layout>
                        {isFeesLoading ? (
                            <OperationInfoSkeleton />
                        ) : (
                            <>
                                <OperationDetail
                                    label={
                                        showFeeInfo ? (
                                            <LabelWithInfo onClick={() => setDialog('fee')}>{t.feeLabel}</LabelWithInfo>
                                        ) : (
                                            t.feeLabel
                                        )
                                    }
                                >
                                    {t.fee}
                                </OperationDetail>
                                <OperationDetail label={t.totalLabel} variant={'total'}>
                                    {t.total}
                                </OperationDetail>
                            </>
                        )}
                    </OperationDetail.Layout>

                    <RsnButton.Primary disabled={!canSubmit} type={'submit'}>
                        Top up
                    </RsnButton.Primary>
                </div>
            </form>

            <StripeErrorDialog error={error} />
            <RsnDialog.Controller isOpen={isLimitsModalOpen} onClose={setIsLimitsModalOpen.off}>
                <CardLimitModal {...limitsOptions} />
            </RsnDialog.Controller>
            <RsnDialog.Controller onClose={closeDialog}>
                {(() =>
                    // fee dialogs
                    (dialog === 'fee' && method?.type === 'card' && <TopupCardFeeDialog account={account} />) ||
                    (dialog === 'fee' && method?.type === 'payment_network' && (
                        <PaymentSystemFeeDialog
                            feeInfo={fee?.feeInfo}
                            paymentNetwork={method?.method}
                            operationType={'deposit'}
                        />
                    )) ||
                    //other dialogs
                    (dialog === 'address' && <AddressDialog />) ||
                    (dialog === 'iban' && <IbanDialog />))()}
            </RsnDialog.Controller>
        </RootLayout>
    );
}
